import { computed } from "vue";
import { useReportCategoriesStoreRefactor } from "@/stores/reportCategoriesStoreRefactor";

/**
 * Composable for getting dynamic report category icons and colors
 * Replaces hardcoded icon/color mappings with database-driven values
 */
export function useReportCategoryIcons() {
  const reportCategoriesStore = useReportCategoriesStoreRefactor();

  // Simple fallback icon and color for categories without custom settings
  const fallbackIcon = "mdi-alert-circle";
  const fallbackColor = "red";

  // Create a computed mapping of category names to their settings
  const categoryIconMap = computed(() => {
    const map = {};
    reportCategoriesStore.reportCategories.forEach((category) => {
      map[category.name] = {
        icon: category.icon || fallbackIcon,
        color: category.color || fallbackColor,
      };
    });
    return map;
  });

  /**
   * Get icon for a report category
   * @param {string} categoryName - Name of the report category
   * @returns {string} MDI icon name
   */
  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return fallbackIcon;

    const categorySettings = categoryIconMap.value[categoryName];
    if (categorySettings) {
      return categorySettings.icon;
    }

    // Fallback if category not found in store
    return fallbackIcon;
  };

  /**
   * Get color for a report category
   * @param {string} categoryName - Name of the report category
   * @returns {string} Color name or hex value
   */
  const getCategoryColor = (categoryName) => {
    if (!categoryName) return fallbackColor;

    const categorySettings = categoryIconMap.value[categoryName];
    if (categorySettings) {
      return categorySettings.color;
    }

    // Fallback if category not found in store
    return fallbackColor;
  };

  /**
   * Get both icon and color for a report category
   * @param {string} categoryName - Name of the report category
   * @returns {object} Object with icon and color properties
   */
  const getCategoryIconAndColor = (categoryName) => {
    return {
      icon: getCategoryIcon(categoryName),
      color: getCategoryColor(categoryName),
    };
  };

  /**
   * Ensure report categories are loaded
   * Call this in components that need category data
   */
  const ensureCategoriesLoaded = async () => {
    if (reportCategoriesStore.reportCategories.length === 0) {
      await reportCategoriesStore.fetchAll();
    }
  };

  return {
    getCategoryIcon,
    getCategoryColor,
    getCategoryIconAndColor,
    ensureCategoriesLoaded,
    categoryIconMap,
  };
}
